import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ViewCarouselOutlinedIcon from "@mui/icons-material/ViewCarouselOutlined";
import Button from "../Buttons/Button";
import {
  BUTTON_GENERATE,
  CAROUSEL,
  CONTENT_RECOMMENDATION_GENERATED,
  CONTENT_RECOMMENDATION_GENERATE_ARIA,
  IMAGE,
  VIDEO,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { GRAY_100, GRAY_200, GRAY_50, GREEN_COLOR } from "../../utils/colors";
import { BORDER_RADIUS } from "../../utils/constants";

const CONTENT_RECOMMENDATION_MEDIA_TYPE_CONFIG = {
  image: {
    Icon: ImageOutlinedIcon,
    labelKey: IMAGE,
  },
  video: {
    Icon: PlayCircleOutlineIcon,
    labelKey: VIDEO,
  },
  carousel: {
    Icon: ViewCarouselOutlinedIcon,
    labelKey: CAROUSEL,
  },
};

const ContentRecommendationItem = ({
  contentRecommendation,
  isCompleted = false,
  onSelect = () => {},
  variant = "full",
}) => {
  const { t } = useTranslationApp();
  const isCompact = variant === "compact";
  const mediaTypeConfig =
    CONTENT_RECOMMENDATION_MEDIA_TYPE_CONFIG[contentRecommendation?.mediaType];
  const MediaTypeIcon = mediaTypeConfig?.Icon;
  const iconBoxSize = isCompact ? 32 : 40;

  const onGenerate = () => {
    onSelect({
      mediaType: contentRecommendation?.mediaType,
      text: contentRecommendation?.text,
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: { xs: "stretch", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5,
        minWidth: 0,
        ...(isCompact
          ? {
              py: 1.25,
              borderBottom: `1px solid ${GRAY_200}`,
            }
          : {
              p: 2,
              border: `1px solid ${GRAY_200}`,
              borderRadius: BORDER_RADIUS,
              bgcolor: isCompleted ? GRAY_50 : "background.paper",
            }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: isCompact ? "center" : "flex-start",
          gap: 1.5,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: iconBoxSize,
            height: iconBoxSize,
            borderRadius: 1.5,
            bgcolor: GRAY_100,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: "text.secondary",
          }}
        >
          {MediaTypeIcon ? (
            <MediaTypeIcon sx={{ fontSize: isCompact ? 18 : 22 }} />
          ) : null}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {mediaTypeConfig ? (
            <Typography
              component="span"
              sx={{
                display: "block",
                fontSize: "11px",
                fontWeight: 600,
                lineHeight: 1.2,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "text.secondary",
                mb: 0.25,
              }}
            >
              {t(mediaTypeConfig.labelKey)}
            </Typography>
          ) : null}
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.6,
              color: isCompleted ? "text.secondary" : "text.primary",
              overflowWrap: "anywhere",
              ...(isCompact && {
                overflow: "hidden",
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: { xs: 3, sm: 2 },
              }),
            }}
          >
            {contentRecommendation?.text}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          width: { xs: "100%", sm: "auto" },
          display: "flex",
          justifyContent: { xs: "flex-start", sm: "center" },
        }}
      >
        {isCompleted ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              border: `1px solid ${GREEN_COLOR}`,
              color: GREEN_COLOR,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            <CheckIcon
              aria-hidden
              sx={{ fontSize: 16, color: `${GREEN_COLOR} !important` }}
            />
            {t(CONTENT_RECOMMENDATION_GENERATED)}
          </Box>
        ) : (
          <Button
            color="secondary"
            size="small"
            type="button"
            ariaLabel={t(CONTENT_RECOMMENDATION_GENERATE_ARIA, {
              text: contentRecommendation?.text,
            })}
            startIcon={<AutoAwesomeIcon sx={{ fontSize: "18px !important" }} />}
            onClick={onGenerate}
            sx={{
              height: 36,
              minWidth: 0,
              py: "6px",
              px: 1.5,
              width: { xs: "100%", sm: "auto" },
              textTransform: "none",
            }}
          >
            {t(BUTTON_GENERATE)}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ContentRecommendationItem;
